import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCopy, faPen, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";

import { useTableSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import { Table, TableColumn } from "@/components/Table/Table";

import { ButtonColor } from "@/components/Button/types";

import { languages } from "@/constants/languages";

import {
	useDeleteDeepResearchPromptMutation,
	useGetDeepResearchPromptsQuery,
} from "@/pages/Private/redux/deepResearch/deepResearch.api";

import { DeepResearchPrompt } from "../schema/deep-research-prompt";
import { UpdateDeepResearchPromptModal } from "./UpdateDeepResearchPromptModal";
import { CreateDeepResearchPromptModal } from "./CreateDeepResearchPromptModal";

export const DeepResearchPromptsTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`templates.deepResearchPrompts.${key}`), [t]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
	const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
	const [selectedPrompt, setSelectedPrompt] = useState<DeepResearchPrompt | null>(null);
	const { page, setPage, limit, setLimit, query } = useTableSearch();
	const [deletePrompt, { isLoading }] = useDeleteDeepResearchPromptMutation();

	const { data, isFetching } = useGetDeepResearchPromptsQuery({
		page,
		limit,
		searchValue: query,
	});

	const handleDeleteModal = (row: DeepResearchPrompt) => {
		setShowDeleteModal(true);
		setSelectedPrompt(row);
	};

	const handleDeletePrompt = async () => {
		if (selectedPrompt?.id) {
			await deletePrompt(selectedPrompt.id).unwrap();
			setShowDeleteModal(false);
			setSelectedPrompt(null);
		}
	};

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: ts("name"),
				uid: "name",
				maxWidth: "200px",
				render: (row: DeepResearchPrompt) => <span className="font-medium">{row.name}</span>,
			},

			{
				title: ts("sections"),
				uid: "sections",
				render: (row: DeepResearchPrompt) => (
					<span className="text-sm">{row.prompt?.sections?.length || 0}</span>
				),
			},
			{
				title: ts("language"),
				uid: "language",
				render: (row: DeepResearchPrompt) => (
					<span className="text-sm">
						{languages.find((language) => language.code === (row.language ?? "en"))?.name ??
							row.language ??
							""}
					</span>
				),
			},
			{
				title: ts("createdAt"),
				uid: "createdAt",
				render: (row: DeepResearchPrompt) => (
					<div>{row.createdAt ? format(new Date(row.createdAt), "HH:mm dd.MM.yyyy") : ""}</div>
				),
			},
			{
				title: "",
				uid: "actions",
				render: (row: DeepResearchPrompt) => (
					<div className="flex items-center gap-2 justify-end">
						<PermissionCheck requiredPermissions={[PermissionRoles.DEEP_RESEARCH]}>
							<button
								className="cursor-pointer"
								onClick={() => {
									setSelectedPrompt(row);
									setShowDuplicateModal(true);
								}}
							>
								<Icon className="text-text-meta w-4 h-4" icon={faCopy} size="lg" />
							</button>
						</PermissionCheck>
						<PermissionCheck requiredPermissions={[PermissionRoles.DEEP_RESEARCH]}>
							<button
								className="cursor-pointer"
								onClick={() => {
									setSelectedPrompt(row);
									setShowUpdateModal(true);
								}}
							>
								<Icon className="text-text-meta w-4 h-4" icon={faPen} size="lg" />
							</button>
						</PermissionCheck>
						<PermissionCheck requiredPermissions={[PermissionRoles.DEEP_RESEARCH]}>
							<button className="cursor-pointer" onClick={() => handleDeleteModal(row)}>
								<img alt="trash" src={trashIcon} />
							</button>
						</PermissionCheck>
					</div>
				),
			},
		],
		[ts]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	return (
		<div className="flex flex-col gap-4 w-full">
			{isFetching && <LoadingOverlay />}
			<div className="w-full">
				<Table
					columns={columns}
					data={data?.data ?? []}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					handleFetchPage={(page) => {
						setPage(page);
					}}
					hidePagination={false}
					limit={limit}
					overallCount={data?.meta?.itemCount}
					page={page}
					sticky={false}
					totalPages={data?.meta?.pageCount}
				/>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeletePrompt}
					isLoading={isLoading}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText="Delete"
					title="Delete Deep Research Prompt"
				>
					<div className="flex flex-row gap-4 items-start p-2 justify-start text-sm mb-2 text-text border border-status-warning rounded-md">
						<Icon className="text-status-warning w-4 h-4" icon={faTriangleExclamation} />
						Are you sure you want to delete this deep research prompt?
					</div>
				</Modal>
			)}
			{showUpdateModal && selectedPrompt && (
				<UpdateDeepResearchPromptModal
					isOpen={showUpdateModal}
					prompt={selectedPrompt}
					setIsOpen={setShowUpdateModal}
				/>
			)}
			{showDuplicateModal && selectedPrompt && (
				<CreateDeepResearchPromptModal
					initialData={selectedPrompt}
					isOpen={showDuplicateModal}
					setIsOpen={setShowDuplicateModal}
				/>
			)}
		</div>
	);
};
